import {
    assignHtmlDatatableColumns,
    waitForCbarDatatable,
    type CbarDatatableContext,
} from '../cbar-datatable';

type DatatableColumn = Record<string, unknown>;
type DatatableElement = HTMLElement & {
    availableSizes?: string[];
    defaultPageSize?: number;
    columns?: DatatableColumn[];
    data?: unknown[];
    deferColumnInference?: boolean;
    requestUpdate?: () => void;
};

type ColumnBuilder = (
    ctx: CbarDatatableContext & { htmlCell: NonNullable<typeof window.CBAR>['datatableHtmlCell'] },
    table: DatatableElement,
) => DatatableColumn[] | void;

function htmlCellFn() {
    return window.CBAR?.datatableHtmlCell || window.CBAR?.payorDatatableHtmlCell;
}

/** Raw data keys cbar-datatable infers when column defs were not applied (campaign-details profile). */
const INFERRED_HTML_COLUMN_HEADERS = new Set([
    'campaign_name',
    'campaign_code',
]);

/** True when cbar-datatable fell back to inferring columns from raw data keys (e.g. invoice_html). */
export function datatableNeedsColumnInit(table: DatatableElement): boolean {
    if (!Array.isArray(table.columns) || table.columns.length === 0) {
        return true;
    }

    return table.columns.some((column) => {
        if (typeof column.header !== 'string') {
            return false;
        }

        const header = column.header.toLowerCase();
        return header.endsWith('_html')
            || header === 'date_sort'
            || header.endsWith('_sort')
            || header === 'is_cancelled'
            || INFERRED_HTML_COLUMN_HEADERS.has(header);
    });
}

export async function waitForDatatableHtmlCell(pollIntervalMs = 50): Promise<NonNullable<ReturnType<typeof htmlCellFn>>> {
    return new Promise((resolve) => {
        const interval = window.setInterval(() => {
            const htmlCell = htmlCellFn();
            if (htmlCell) {
                window.clearInterval(interval);
                resolve(htmlCell);
            }
        }, pollIntervalMs);
    });
}

function actionsCellFn() {
    return (
        window.CBAR?.datatableInlineActionsCell ||
        window.CBAR?.datatableActionsCell ||
        htmlCellFn()
    );
}

export async function waitForDatatableElement(table: DatatableElement): Promise<void> {
    table.deferColumnInference = true;
    await customElements.whenDefined('cbar-datatable');
    const DatatableClass = customElements.get('cbar-datatable');
    if (DatatableClass && !(table instanceof DatatableClass)) {
        customElements.upgrade(table);
    }
    const litTable = table as DatatableElement & {updateComplete?: Promise<unknown>};
    if (litTable.updateComplete) {
        await litTable.updateComplete;
    }
}

export async function setupDatatable(tableId: string, buildColumns: ColumnBuilder): Promise<void> {
    const table = document.getElementById(tableId) as DatatableElement | null;
    if (!table || table.dataset.serverSide === 'true') {
        return;
    }

    await waitForDatatableElement(table);

    const ctx = await waitForCbarDatatable();
    const htmlCell = await waitForDatatableHtmlCell();

    const result = buildColumns({...ctx, htmlCell}, table);
    if (Array.isArray(result)) {
        assignHtmlDatatableColumns(table, result);
    }
}

export async function setupDatatables(configs: Array<[string, ColumnBuilder]>): Promise<void> {
    await Promise.allSettled(configs.map(([tableId, buildColumns]) => setupDatatable(tableId, buildColumns)));
}

export function textColumn(
    ch: CbarDatatableContext['columnHelper'],
    key: string,
    header: string,
    options: DatatableColumn = {},
): DatatableColumn {
    return ch.accessor(key, {
        header: () => header,
        enableSorting: false,
        ...options,
    });
}

/**
 * Sort by an ISO `*_sort` field while displaying a friendly date string.
 * Use when the row has both `displayKey` (shown) and `sortKey` (Y-m-d[/H:i:s]).
 */
export function sortableDateColumn(
    ch: CbarDatatableContext['columnHelper'],
    sortKey: string,
    header: string,
    displayKey: string,
    options: DatatableColumn = {},
): DatatableColumn {
    return ch.accessor(sortKey, {
        header: () => header,
        cell: (info: {row: {original: Record<string, unknown>}}) => {
            const value = info.row.original[displayKey];
            return value == null ? '' : String(value);
        },
        ...options,
    });
}

/**
 * Like {@link sortableDateColumn}, but renders HTML from the display field.
 */
export function sortableUnsafeHtmlDateColumn(
    ch: CbarDatatableContext['columnHelper'],
    html: CbarDatatableContext['html'],
    unsafeHTML: NonNullable<typeof window.CBAR>['unsafeHTML'],
    sortKey: string,
    header: string,
    displayKey: string,
    options: DatatableColumn = {},
): DatatableColumn {
    return ch.accessor(sortKey, {
        header: () => header,
        cell: (info: {row: {original: Record<string, unknown>}}) => {
            const value = info.row.original[displayKey];
            if (value == null || value === '') {
                return '';
            }
            return html`${unsafeHTML!(String(value))}`;
        },
        ...options,
    });
}

export function htmlColumn(
    ch: CbarDatatableContext['columnHelper'],
    htmlCell: NonNullable<ReturnType<typeof htmlCellFn>>,
    key: string,
    header: string,
    options: DatatableColumn = {},
): DatatableColumn {
    return ch.accessor(key, {
        header: () => header,
        cell: (info: { getValue: () => unknown }) => htmlCell(info.getValue()),
        enableSorting: false,
        ...options,
    });
}

export function unsafeHtmlColumn(
    ch: CbarDatatableContext['columnHelper'],
    html: CbarDatatableContext['html'],
    unsafeHTML: NonNullable<typeof window.CBAR>['unsafeHTML'],
    key: string,
    header: string,
    options: DatatableColumn = {},
): DatatableColumn {
    return ch.accessor(key, {
        header: () => header,
        cell: (info: { getValue: () => unknown }) => html`${unsafeHTML!(info.getValue() as string)}`,
        enableSorting: false,
        ...options,
    });
}

export function actionColumn(
    ch: CbarDatatableContext['columnHelper'],
    _htmlCell: NonNullable<ReturnType<typeof htmlCellFn>>,
    key: string,
): DatatableColumn {
    const actionsCell = actionsCellFn();
    return htmlColumn(ch, actionsCell ?? _htmlCell, key, '', {enableColumnFilter: false});
}

export function unsafeActionColumn(
    ch: CbarDatatableContext['columnHelper'],
    html: CbarDatatableContext['html'],
    unsafeHTML: NonNullable<typeof window.CBAR>['unsafeHTML'],
    key: string,
): DatatableColumn {
    return unsafeHtmlColumn(ch, html, unsafeHTML, key, '', {enableColumnFilter: false});
}

export type {DatatableColumn, DatatableElement, ColumnBuilder};
